import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Stack, CircularProgress,
    FormControl, InputLabel, Select, Typography, Box, ListSubheader
} from '@mui/material';
import { format } from 'date-fns';
import { createEvent } from '@firebasegen/default-connector';
import { dataConnect } from '../firebaseConfig';
import { EventTypes, EventTypeGroup } from '../constants/common';
import { useArtists } from '../contexts/ArtistContext';
import useRefreshData from '../hooks/useRefreshData';

const BulkCreateEventsModal = ({ open, handleClose, initialArtistId, initialStartDate, initialEndDate, onCreated }) => {
    const [startDate, setStartDate] = useState(initialStartDate || '');
    const [endDate, setEndDate] = useState(initialEndDate || '');
    const [eventType, setEventType] = useState('STUDIO');
    const [eventName, setEventName] = useState('');
    const [loading, setLoading] = useState(false);
    const { refreshData } = useRefreshData();

    const [artists, setArtists] = useState([]);
    const [artistId, setArtistId] = useState(initialArtistId || "");

    const { data: artistsData } = useArtists({ enabled: open });
    // Dates picked by dragging across the calendar
    useEffect(() => {
        if (open && initialStartDate) {
            setStartDate(initialStartDate);
            setEndDate(initialEndDate || initialStartDate);
        }
    }, [open, initialStartDate, initialEndDate]);

    useEffect(() => {
        if (artistsData && artistsData.length > 0) {
            setArtists(artistsData);
            if (!artistId) setArtistId(artistsData[0].id);
        }
    }, [artistsData, open]);

    const isInvalidDates = () => {
        if (!startDate || !endDate) return true;
        const start = new Date(startDate);
        const end = new Date(endDate);
        return start > end;
    };

    const handleSubmit = async () => {
        if (!startDate || !endDate || !artistId) return;

        setLoading(true);
        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const datesToCreate = [];

            let current = new Date(start);
            while (current <= end) {
                datesToCreate.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }

            for (const date of datesToCreate) {
                const newEvent = {
                    artistId: artistId,
                    date: format(date, 'yyyy-MM-dd'),
                    name: eventName.trim() || EventTypes.find(t => t.value === eventType)?.name || 'Event',
                    type: eventType,
                    status: 'PENDING', // Default status
                    timeZone: 'Europe/Oslo'
                };
                await createEvent(dataConnect, newEvent);
            }

            if (onCreated) onCreated();
            refreshData(artistId);
            setStartDate('');
            setEndDate('');
            setEventType('STUDIO');
            setEventName('');
            handleClose();
        } catch (error) {
            console.error('Failed to create bulk events', error);
            alert('Encountered an error: ' + error.message);
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Multiple Events</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>

                    {artists.length > 1 && (
                        <FormControl fullWidth>
                            <InputLabel>Artist</InputLabel>
                            <Select
                                value={artistId}
                                onChange={(e) => setArtistId(e.target.value)}
                                label="Artist"
                            >
                                {artists.map((artist) => (
                                    <MenuItem key={artist.id} value={artist.id}>
                                        {artist.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {artists.length === 0 && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            No artists found. Please add an artist first.
                        </Typography>
                    )}

                    <TextField
                        label="Name"
                        fullWidth
                        value={eventName}
                        onChange={e => setEventName(e.target.value)}
                        placeholder="Optional – defaults to event type"
                    />
                    <TextField
                        label="From Date"
                        type="date"
                        fullWidth
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        fullWidth
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        select
                        label="Event Type"
                        fullWidth
                        value={eventType}
                        onChange={e => setEventType(e.target.value)}
                    >
                        {Object.entries(
                            EventTypes.reduce((acc, type) => {
                                const group = type.group || 'OTHER';
                                if (!acc[group]) acc[group] = [];
                                acc[group].push(type);
                                return acc;
                            }, {})
                        ).map(([group, types]) => [
                            <ListSubheader key={`header-${group}`} sx={{ fontWeight: 'bold', lineHeight: '36px', bgcolor: 'background.paper' }}>
                                {EventTypeGroup[group]?.name || 'Other'}
                            </ListSubheader>,
                            ...types.map(type => (
                                <MenuItem key={type.value} value={type.value} sx={{ pl: 4 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {type.icon}
                                        <span>{type.name}</span>
                                    </Stack>
                                </MenuItem>
                            ))
                        ])}
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isInvalidDates() || loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? 'Creating...' : 'Create Events'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkCreateEventsModal;
